import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useSession } from "../../context/AuthContext";
import api from "../../services/api";
import Dialog from "./Dialog";

function getYouTubeId(url: string): string | null {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|shorts\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
}

interface FeedItem {
  id: string;
  type: string;
  content: string;
  metadata?: string;
  author: { id?: string; name: string; role: string };
  createdAt?: string;
  created_at?: string;
}

export default function UnifiedFeed() {
  const { data: session }: any = useSession();
  const [items, setItems] = useState<FeedItem[]>([]);
  const [newPost, setNewPost] = useState("");
  const [postType, setPostType] = useState<"TEXT" | "EVENT" | "VIDEO" | "OFFER">("TEXT");
  const [linkUrl, setLinkUrl] = useState("");
  const [isPosting, setIsPosting] = useState(false);
  const [isPostDialogOpen, setIsPostDialogOpen] = useState(false);
  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  const [editingOgImage, setEditingOgImage] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showGoToTop, setShowGoToTop] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => setMounted(true), []);

  const fetchFeed = async (targetPage = page) => {
    try {
      const res = await api.get(`/feed?page=${targetPage}&limit=10`);
      if (targetPage === 1) {
        setItems(res.data);
      } else {
        setItems(prev => [...prev, ...res.data]);
      }
      setHasMore(res.data.length === 10);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (page === 1) {
      fetchFeed(1);
    }
  }, [page]);

  // Polling for new updates on page 1 only
  useEffect(() => {
    const interval = setInterval(() => {
      if (page === 1) {
        fetchFeed(1);
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [page]);

  const handlePost = async () => {
    if (!newPost.trim() || isPosting) return;
    setIsPosting(true);
    try {
      if (editingPostId) {
        await api.post(`/feed/${editingPostId}`, {
          _method: 'PATCH',
          content: newPost,
          authorId: session?.user?.id,
          metadata: { postType, linkUrl }
        });
      } else {
        await api.post(`/feed`, {
          content: newPost,
          authorId: session?.user?.id,
          type: session?.user?.role === "ADMIN" ? "SYSTEM" : "COACH",
          metadata: { postType, linkUrl }
        });
      }
      resetModal();
      setPage(1);
      fetchFeed(1);
    } catch (err) {
      console.error(err);
    } finally {
      setIsPosting(false);
    }
  };

  const resetModal = () => {
    setNewPost("");
    setLinkUrl("");
    setPostType("TEXT");
    setEditingPostId(null);
    setEditingOgImage(null);
    setIsPostDialogOpen(false);
  };

  const handleEditClick = (post: FeedItem) => {
    setEditingPostId(post.id);
    let meta: any = null;
    try {
      if (post.metadata) meta = typeof post.metadata === "string" ? JSON.parse(post.metadata) : post.metadata;
    } catch(e) {}
    
    setPostType(meta?.postType || "TEXT");
    setLinkUrl(meta?.linkUrl || "");
    setEditingOgImage(meta?.ogImage || null);
    setNewPost(post.content || "");
    setIsPostDialogOpen(true);
  };

  const handleDeleteClick = (postId: string) => {
    setDeleteConfirmId(postId);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmId) return;
    try {
      await api.post(`/feed/${deleteConfirmId}?authorId=${session?.user?.id}&_method=DELETE`);
      setItems(prev => prev.filter(p => p.id !== deleteConfirmId));
    } catch (err) {
      console.error("Failed to delete", err);
    } finally {
      setDeleteConfirmId(null);
    }
  };

  const handleLoadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const nextPage = page + 1;
    setPage(nextPage);
    await fetchFeed(nextPage);
    setLoadingMore(false);
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    
    // Toggle Go to Top button visibility when user scrolls down
    if (target.scrollTop > 300) {
      setShowGoToTop(true);
    } else {
      setShowGoToTop(false);
    }

    const isAtBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 50;
    if (isAtBottom && hasMore && !loadingMore) {
      handleLoadMore();
    }
  };

  const handleGoToTop = () => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const userRole = session?.user?.role;
  const canPost = userRole === "ADMIN" || userRole === "COACH" || userRole === "COACHING_ASSOCIATE";

  return (
    <div className="relative bg-surface-container-low p-7 rounded-[2.5rem] border border-outline-variant/10 shadow-sm flex flex-col h-[680px] space-y-8 overflow-hidden">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-black text-on-surface tracking-tight">📢 Coach Announcements</h3>
        <span className="material-symbols-outlined text-slate-400 text-sm">sensors</span>
      </div>

      {canPost && (
        <button 
          onClick={() => {
            setEditingPostId(null);
            setNewPost("");
            setLinkUrl("");
            setPostType("TEXT");
            setIsPostDialogOpen(true);
          }}
          className="w-full py-3 border border-outline-variant/10 text-on-surface rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-surface-container-high active:scale-[0.98] transition-all flex items-center justify-center gap-2 mb-2 cursor-pointer"
        >
          <span className="material-symbols-outlined text-sm">add</span>
          Post Update
        </button>
      )}

      <div 
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="space-y-8 flex-1 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-outline-variant/20 scroll-smooth"
      >
        {items.length === 0 && (
          <p className="text-center py-12 text-on-surface-variant/40 text-[10px] font-black uppercase tracking-[0.2em] italic">No updates yet...</p>
        )}
        {items.map((post) => {
          let meta: any = null;
          try {
            if (post.metadata) {
              meta = typeof post.metadata === "string" ? JSON.parse(post.metadata) : post.metadata;
            }
          } catch(e) {}
          
          const isEvent = meta?.postType === "EVENT";
          const isVideo = meta?.postType === "VIDEO";
          const isOffer = meta?.postType === "OFFER";
          const hasLink = !!meta?.linkUrl;

          return (
            <div key={post.id} className="relative pl-7 border-l-2 border-outline-variant/10 pb-2 text-left">
              {/* Dot indicator */}
              <div className={`absolute left-[-5px] top-0 w-2 h-2 rounded-full shadow-sm ${
                post.author.role === "ADMIN" ? "bg-red-500 shadow-red-500/20" : 
                post.author.role === "COACH" ? "bg-blue-500 shadow-blue-500/20" : 
                "bg-emerald-500 shadow-emerald-500/20"
              }`} />
              
              <div className="flex items-center justify-between mb-1 group/header">
                <div className="flex items-center gap-2">
                  <span className={`text-[9px] font-black uppercase tracking-widest ${
                     post.author.role === "ADMIN" ? "text-red-500" : 
                     post.author.role === "COACH" ? "text-blue-500" : "text-emerald-500"
                  }`}>
                    {isEvent ? "New Event" : isVideo ? "New Video" : isOffer ? "Special Offer" : `${post.author.role} Update`}
                  </span>
                  <span className="text-[9px] font-bold text-on-surface-variant opacity-40 uppercase">
                    • {new Date(post.created_at || post.createdAt || "").toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                
                {/* Actions for authorized users */}
                {(session?.user?.role === "ADMIN" || session?.user?.id === post.author?.id) && (
                  <div className="flex gap-3 opacity-0 group-hover/header:opacity-100 transition-opacity">
                    <button onClick={() => handleEditClick(post)} className="text-on-surface-variant hover:text-blue-500 transition-colors cursor-pointer" title="Edit">
                      <span className="material-symbols-outlined text-[14px]">edit</span>
                    </button>
                    <button onClick={() => handleDeleteClick(post.id)} className="text-on-surface-variant hover:text-red-500 transition-colors cursor-pointer" title="Delete">
                      <span className="material-symbols-outlined text-[14px]">delete</span>
                    </button>
                  </div>
                )}
              </div>
              
              <p className="text-xs font-black text-on-surface mb-1">{post.author.name}</p>
              <p className="text-xs text-on-surface-variant leading-relaxed font-medium whitespace-pre-wrap">{post.content}</p>

              {hasLink && (
                <a href={meta.linkUrl} target="_blank" rel="noopener noreferrer" className="mt-3 block bg-surface-container-lowest border border-outline-variant/10 rounded-xl p-3 hover:bg-surface-container-high transition-colors group overflow-hidden">
                  
                  {/* Video Thumbnail Preview */}
                  {isVideo && (() => {
                    const ytId = getYouTubeId(meta.linkUrl);
                    const isDirectVideo = /\.(mp4|webm|ogg|mov)$/i.test(meta.linkUrl);

                    if (ytId) {
                      return (
                        <div className="relative aspect-video w-full rounded-lg overflow-hidden border border-outline-variant/5 mb-3 group-hover:shadow-md transition-shadow">
                          <img 
                            src={`https://img.youtube.com/vi/${ytId}/hqdefault.jpg`} 
                            alt="Video Preview"
                            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                            <div className="w-10 h-10 rounded-full bg-white/90 group-hover:bg-white text-red-600 flex items-center justify-center shadow-md group-hover:scale-110 transition-all duration-300">
                              <span className="material-symbols-outlined text-xl fill-1">play_arrow</span>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    if (isDirectVideo) {
                      return (
                        <div className="relative aspect-video w-full rounded-lg overflow-hidden border border-outline-variant/5 mb-3 bg-black group-hover:shadow-md transition-shadow">
                          <video 
                            src={meta.linkUrl} 
                            preload="metadata"
                            className="w-full h-full object-cover pointer-events-none" 
                          />
                          <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                            <div className="w-10 h-10 rounded-full bg-white/90 group-hover:bg-white text-red-600 flex items-center justify-center shadow-md group-hover:scale-110 transition-all duration-300">
                              <span className="material-symbols-outlined text-xl fill-1">play_arrow</span>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    // OpenGraph Image Preview (Vimeo, Events, external blogs)
                    if (meta.ogImage) {
                      return (
                        <div className="relative aspect-video w-full rounded-lg overflow-hidden border border-outline-variant/5 mb-3 bg-black group-hover:shadow-md transition-shadow">
                          <img 
                            src={meta.ogImage} 
                            alt="Link Preview"
                            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                          />
                          {isVideo && (
                            <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                              <div className="w-10 h-10 rounded-full bg-white/90 group-hover:bg-white text-red-600 flex items-center justify-center shadow-md group-hover:scale-110 transition-all duration-300">
                                <span className="material-symbols-outlined text-xl fill-1">play_arrow</span>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    }

                    // Default elegant preview fallback if video type is unknown/generic
                    return (
                      <div className="relative aspect-video w-full rounded-lg overflow-hidden border border-outline-variant/5 mb-3 bg-gradient-to-br from-slate-900 to-slate-800 flex flex-col items-center justify-center gap-1.5 shadow-inner">
                        <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors animate-pulse" />
                        <span className="material-symbols-outlined text-2xl text-red-500 relative z-10 group-hover:scale-110 transition-transform">play_circle</span>
                        <span className="text-[8px] font-black uppercase tracking-widest text-slate-400 relative z-10">Stream Video Content</span>
                      </div>
                    );
                  })()}

                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      isEvent ? "bg-purple-500/10 text-purple-500" :
                      isVideo ? "bg-red-500/10 text-red-500" :
                      isOffer ? "bg-green-500/10 text-green-500" :
                      "bg-blue-500/10 text-blue-500"
                    }`}>
                      <span className="material-symbols-outlined text-[16px]">
                        {isEvent ? "event" : isVideo ? "play_arrow" : isOffer ? "chat" : "link"}
                      </span>
                    </div>
                    <div className="flex-1 overflow-hidden">
                      <p className={`text-[10px] font-black uppercase tracking-widest truncate group-hover:opacity-80 transition-opacity ${
                        isEvent ? "text-purple-500" :
                        isVideo ? "text-red-500" :
                        isOffer ? "text-green-500" :
                        "text-blue-500"
                      }`}>
                        {isEvent ? "RSVP / View Event" : isVideo ? "Watch Video" : isOffer ? "Message on WhatsApp" : "External Link"}
                      </p>
                      <p className="text-[9px] text-on-surface-variant truncate opacity-60 mt-0.5">{meta.linkUrl}</p>
                    </div>
                    <span className="material-symbols-outlined text-on-surface-variant opacity-20 group-hover:opacity-60 transition-opacity text-[16px]">open_in_new</span>
                  </div>
                </a>
              )}
            </div>
          );
        })}

        {hasMore && (
          <div className="pt-4 flex justify-center animate-fade-in">
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="px-6 py-3 rounded-xl bg-surface-container-high hover:bg-surface-container-highest disabled:opacity-30 disabled:hover:bg-surface-container-high text-[10px] font-black uppercase tracking-widest transition-all cursor-pointer text-on-surface flex items-center gap-2 border border-outline-variant/10 shadow-sm"
            >
              {loadingMore ? (
                <span className="w-3.5 h-3.5 border-2 border-on-surface/20 border-t-on-surface rounded-full animate-spin" />
              ) : (
                <span className="material-symbols-outlined text-sm">keyboard_arrow_down</span>
              )}
              {loadingMore ? "Loading..." : "Load More Posts"}
            </button>
          </div>
        )}
      </div>

      {showGoToTop && (
        <button
          onClick={handleGoToTop}
          className="absolute bottom-6 right-8 w-11 h-11 rounded-full bg-primary text-white shadow-lg hover:shadow-xl hover:bg-primary/95 flex items-center justify-center transition-all duration-300 scale-100 hover:scale-110 active:scale-95 cursor-pointer z-50 animate-in fade-in zoom-in-75"
          title="Go to Top"
        >
          <span className="material-symbols-outlined text-xl font-bold">arrow_upward</span>
        </button>
      )}

      {isPostDialogOpen && mounted && createPortal(
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4 backdrop-blur-sm">
          {/* Modal content omitted for brevity... it remains unchanged */}
          <div className="bg-surface-container-low p-6 sm:p-10 rounded-[2.5rem] border border-outline-variant/20 shadow-2xl max-w-xl w-full relative animate-in zoom-in-95 duration-300 max-h-[90vh] overflow-y-auto scrollbar-thin scrollbar-thumb-outline-variant/20">
            <button 
              onClick={resetModal}
              className="absolute top-6 right-6 w-8 h-8 flex items-center justify-center rounded-full bg-surface-container hover:bg-surface-container-high text-on-surface-variant hover:text-on-surface transition-all cursor-pointer z-10"
            >
              <span className="material-symbols-outlined text-sm">close</span>
            </button>
            
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
                <span className="material-symbols-outlined text-2xl">{editingPostId ? 'edit_square' : 'campaign'}</span>
              </div>
              <h3 className="text-2xl font-black text-on-surface tracking-tight">
                {editingPostId ? "Edit Update" : "Create Update"}
              </h3>
            </div>
            
            <div className="space-y-6">
              {/* Type Selection - Full Width Cards with Hints */}
              <div className="flex flex-col gap-3">
                {[
                  { 
                    id: "TEXT", label: "Normal Text", icon: "article", 
                    hint: "Share general announcements, quick tips, or daily motivation with students." 
                  },
                  { 
                    id: "EVENT", label: "Event Link", icon: "event", 
                    hint: "Invite students to upcoming live classes, Q&A sessions, or webinars." 
                  },
                  { 
                    id: "VIDEO", label: "Video Link", icon: "play_circle", 
                    hint: "Suggest deep-dive masterclasses, YouTube tutorials, or recorded sessions." 
                  },
                  { 
                    id: "OFFER", label: "Special Offer", icon: "local_offer", 
                    hint: "Promote exclusive 1-on-1 coaching, upgrades, or limited-time discounts." 
                  }
                ].map((type) => (
                  <button
                    key={type.id}
                    onClick={() => setPostType(type.id as any)}
                    className={`flex items-start gap-4 p-4 rounded-2xl text-left transition-all border cursor-pointer w-full group ${
                      postType === type.id 
                        ? "bg-primary/5 border-primary shadow-sm" 
                        : "bg-surface-container-lowest border-outline-variant/20 hover:border-outline-variant hover:bg-surface-container-low"
                    }`}
                  >
                    <span className={`material-symbols-outlined text-2xl mt-0.5 transition-colors ${
                      postType === type.id ? "text-primary" : "text-on-surface-variant group-hover:text-on-surface"
                    }`}>
                      {type.icon}
                    </span>
                    <div className="flex flex-col">
                      <span className={`text-sm font-black transition-colors ${
                        postType === type.id ? "text-primary" : "text-on-surface"
                      }`}>
                        {type.label}
                      </span>
                      <span className="text-[11px] font-medium text-on-surface-variant leading-relaxed mt-1 opacity-80">
                        {type.hint}
                      </span>
                    </div>
                  </button>
                ))}
              </div>

              {/* URL Input (Full Width) with Dynamic Preview */}
              {postType !== "TEXT" && (
                <div className="animate-in slide-in-from-top-2 fade-in duration-200">
                  <input 
                    type="text" 
                    placeholder={postType === "OFFER" ? "WhatsApp wa.me Link..." : "Enter URL..."}
                    value={linkUrl}
                    onChange={(e) => setLinkUrl(e.target.value)}
                    className="w-full bg-surface-container-lowest border border-outline-variant/20 rounded-xl px-5 py-4 text-sm font-medium outline-none text-on-surface focus:border-primary/50 focus:ring-2 focus:ring-primary/20 transition-all placeholder:text-on-surface-variant/40 shadow-inner shadow-black/5"
                  />
                  
                  {/* Inline Modal Preview */}
                  {(() => {
                    if (!linkUrl) return null;
                    const ytId = getYouTubeId(linkUrl);
                    const previewSrc = ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : editingOgImage;
                    
                    if (!previewSrc) return null;
                    return (
                      <div className="mt-4 relative aspect-video w-full rounded-xl overflow-hidden border border-outline-variant/10 shadow-sm animate-in fade-in zoom-in-95 duration-300">
                        <img 
                          src={previewSrc} 
                          alt="Link Preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    );
                  })()}
                </div>
              )}
              
              {/* Textarea (Full Width) */}
              <div className="relative">
                <textarea 
                  placeholder={postType === "OFFER" ? "Describe the special offer..." : postType === "EVENT" ? "Describe the upcoming event..." : postType === "VIDEO" ? "Describe the video..." : "Write a new update for the community..."}
                  className="w-full bg-surface-container-lowest border border-outline-variant/20 rounded-2xl p-5 text-sm font-medium focus:border-primary/50 focus:ring-2 focus:ring-primary/20 outline-none transition-all resize-none h-40 text-on-surface placeholder:text-on-surface-variant/40 shadow-inner shadow-black/5"
                  value={newPost}
                  onChange={(e) => setNewPost(e.target.value)}
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button 
                  onClick={resetModal}
                  className="flex-1 py-4 bg-surface-container hover:bg-surface-container-high text-on-surface rounded-xl text-[11px] font-black uppercase tracking-widest transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button 
                  onClick={handlePost}
                  disabled={!newPost.trim() || isPosting}
                  className="flex-[2] py-4 bg-primary text-on-primary rounded-xl text-[11px] font-black uppercase tracking-widest hover:opacity-90 hover:shadow-lg hover:shadow-primary/20 transition-all disabled:opacity-50 disabled:hover:shadow-none cursor-pointer flex items-center justify-center gap-2"
                >
                  {isPosting ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined text-[16px]">{editingPostId ? 'save' : 'send'}</span>
                      {editingPostId ? "Save Changes" : "Post Update"}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
      {/* Delete Confirmation Dialog */}
      <Dialog 
        isOpen={deleteConfirmId !== null} 
        onClose={() => setDeleteConfirmId(null)} 
        title="Confirm Deletion"
      >
        <p className="text-sm text-on-surface-variant mb-6">Are you sure you want to delete this update?</p>
        <div className="flex justify-end gap-3">
          <button 
            onClick={() => setDeleteConfirmId(null)} 
            className="px-4 py-2 text-sm font-bold text-on-surface hover:bg-surface-container-high rounded-xl transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button 
            onClick={confirmDelete} 
            className="px-4 py-2 text-sm font-bold bg-red-500 hover:bg-red-600 text-white rounded-xl transition-colors cursor-pointer"
          >
            Delete
          </button>
        </div>
      </Dialog>
    </div>
  );
}
